import type { AppRouteType } from "./routing.types";

/**
 * Recursively searches for the full path of a given route key in the RouterLinks object.
 *
 * @param routeKey - The key of the route to find (e.g., 'home', 'trustedAccounts').
 * @param routes - The object of routes to search within (default as routes).
 * @param parentPath - The accumulated path from parent routes (used for recursive searches).
 *
 * @returns {string} - The full path of the route if found, or null if not found.
 *
 */
const findPathByRouteKey = (
	routeKey: string,
	routes: AppRouteType[],
	parentPath = "",
): string | null => {
	for (const route of routes) {
		const fullPath = `${parentPath}${route.href}`;

		if (route.name.toLowerCase() === routeKey.toLowerCase()) {
			return fullPath;
		}

		if (route.pages) {
			const subRoutes = findPathByRouteKey(
				routeKey,
				route.pages,
				fullPath,
			);
			if (subRoutes) {
				return subRoutes;
			}
		}
	}

	return null;
};

/**
 * find the route path by route key
 *
 * @param routeKey - The key of the route to find (e.g., 'home', 'trustedAccounts').
 *
 * @returns {string} - The full path of the route if found, or home if not found.
 *
 * @example - getRoutePath('settlementAccounts') - return /compliance/settlement-accounts
 */
export const getRoutePath = (
	routeKey: string,
	routes: AppRouteType[],
): string => {
	const topLevelRoute = routes.find(
		(route) => route.name.toLowerCase() === routeKey.toLowerCase(),
	);

	if (topLevelRoute) {
		return topLevelRoute.href;
	}

	return findPathByRouteKey(routeKey, routes, "") || "/";
};

/**
 * Retrieves the subpages of a route based on its path.
 *
 * @param {string} path - The path of the route to find.
 * @param {AppRouteType[]} routes - (Optional) The list of routes to search within. Defaults as routes.
 * @returns {AppRouteType[] | []} - Returns an array of subpages if found, otherwise null.
 */
export const getSubRoutes = (
	path: string,
	routes: AppRouteType[],
): AppRouteType[] => {
	for (const route of routes) {
		if (route.href === path) {
			return route.pages || [];
		}

		if (route.pages) {
			const subRoutes = getSubRoutes(path, route.pages);
			if (subRoutes && subRoutes.length > 0) {
				return subRoutes;
			}
		}
	}

	return [];
};
